import { Modal } from "antd";
import React, { useEffect, useRef, useState } from "react";
import WebUtils from "../../utils/WebUtils";
import Icon from "../../../assets/Icon";
import { updateConversationHistoryApi } from "../../../api";
import { Loader2 } from "lucide-react";

function MonitorDisconnectedModal({
  monitorDisconnectModal,
  interviewData,
  videoBlobQueueRef,
  screenShareBlobQueueRef,
  timeRemainingRef,
  chatMessagesRef,
  stopRecording,
  isHealthOk,
  setMonitorDisconnectModal,
  allowUnloadRef,
}) {
  const elapsedSecondsRef = useRef(0);
  const updateApiFlag = useRef(false);
  const endIntervalRef = useRef(null);

  const [countdown, setCountdown] = useState(120);

  const downloadWindowsMonitorFile = (windows = true) => {
    allowUnloadRef.current = true;
    let url;
    if (windows) {
      url =
        "https://hireplusplus-public-in.s3.ap-south-1.amazonaws.com/monitoringTool/windows/HPPMonitoringTool.exe";
    } else {
      url =
        "https://hireplusplus-public-in.s3.ap-south-1.amazonaws.com/monitoringTool/mac/HPPMonitoringTool.dmg";
    }

    const link = document.createElement("a");
    link.href = url;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    allowUnloadRef.current = false;
  };

  const endInterviewFunc = () => {
    elapsedSecondsRef.current += 1;

    // decrease countdown every 1 second (2 × 500ms)
    if (elapsedSecondsRef.current % 2 === 0) {
      setCountdown((prev) => Math.max(prev - 1, 0));
    }

    // 240 × 500ms = 120 seconds
    if (
      elapsedSecondsRef.current >= 240 &&
      videoBlobQueueRef.current.length === 0 &&
      screenShareBlobQueueRef.current.length === 0 &&
      updateApiFlag.current
    ) {
      stopInterval();
      window.location.href = `/preview?interviewId=${interviewData?.interviewId}`;
    }
  };

  const startInterval = () => {
    if (!endIntervalRef.current) {
      endIntervalRef.current = setInterval(endInterviewFunc, 500);
    }
  };

  const stopInterval = () => {
    if (endIntervalRef.current) {
      clearInterval(endIntervalRef.current);
      endIntervalRef.current = null;
    }
  };

  const resetCountdown = () => {
    elapsedSecondsRef.current = 0;
    setCountdown(120);
  };

  const updateConverstionHistory = () => {
    const url = updateConversationHistoryApi;

    const requestBody = {
      interviewId: interviewData?.interviewId,
      interviewRemainingTime: timeRemainingRef.current,
      conversation: chatMessagesRef.current,
    };

    WebUtils.httpOperations(url, requestBody, "POST")
      .catch((err) => console.log(err))
      .finally(() => {
        updateApiFlag.current = true;
      });
  };

  // initial setup
  useEffect(() => {
    updateConverstionHistory();

    setTimeout(() => {
      startInterval();
    }, 1000);

    return () => stopInterval();
  }, []);

  // monitor health changes
  useEffect(() => {
    if (isHealthOk) {
      stopInterval();
      resetCountdown();
    } else {
      startInterval();
    }
  }, [isHealthOk]);

  return (
    <Modal
      className="rejoin-modal"
      open={monitorDisconnectModal}
      centered
      footer={null}
      closeIcon={null}
      width={520}
    >
      <div className="flex gap-3 items-center">
        <Icon type="warning-icon" width="45" height="45" />
        <div className="mt-[.15rem] font-semibold text-[1.1rem] text-[#101828]">
          Monitor Tool Not Detected
        </div>
      </div>

      {/* MESSAGE */}
      <p className="mt-3 text-[0.88rem] text-[#475467] font-medium leading-[1.4rem]">
        The interview monitoring tool must be running to continue. Please start{" "}
        <b>monitor.exe</b> and keep it running throughout your interview.
      </p>

      {/* DOWNLOAD */}
      <div className="mt-3 text-[0.88rem] text-[#475467] font-medium">
        Download for{" "}
        <span
          onClick={() => downloadWindowsMonitorFile(true)}
          className="text-[#53A0F8] font-semibold cursor-pointer hover:text-blue-400 underline"
        >
          Windows
        </span>{" "}
        | Download for{" "}
        <span
          onClick={() => downloadWindowsMonitorFile(false)}
          className="text-[#53A0F8] font-semibold cursor-pointer hover:text-blue-400 underline"
        >
          Mac
        </span>
      </div>

      {/* COUNTDOWN */}
      {!isHealthOk && (
        <div className="mt-[.8rem] mb-[-.5rem] text-sm text-red-500">
          Redirecting to preview screen in <b>{countdown}s</b> if monitor tool
          is not detected.
        </div>
      )}

      {/* CHECKING STATUS */}
      <div className="mt-3 flex flex-col md:flex-row gap-3 flex-wrap items-center">
        <div className="w-full md:w-[52%]">
          {!isHealthOk ? (
            <div className="text-sm text-[#475467] font-medium flex gap-[.35rem] items-center">
              Fetching monitor tool status..
              <Loader2
                color="#53A0F8"
                strokeWidth={2.8}
                size={21}
                className="animate-spin"
              />
            </div>
          ) : (
            <div className="font-medium text-[#475467] flex gap-[.65rem] items-center">
              Monitor Status:
              <span className="border border-gray-200 shadow-sm px-3 py-1 text-[.82rem] font-semibold rounded-md bg-green-100 text-green-700">
                Detected
              </span>
            </div>
          )}
        </div>

        {/* BUTTON */}
        <div className="w-full md:flex-1">
          <button
            disabled={!isHealthOk}
            className={`${
              isHealthOk ? "btn-blue" : "btn-disabled"
            } w-full px-5 py-2 text-[0.95rem]`}
            onClick={() => setMonitorDisconnectModal(false)}
          >
            Continue Interview
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default MonitorDisconnectedModal;
